import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import leadService from '../../../api/crm/leads/leadService';
import opportunityService from '../../../api/crm/opportunities/opportunityService';
import OpportunityEditModal from '../opportunity-pipeline/components/OpportunityEditModal';
import {
  ArrowRight,
  ArrowLeft,
  Target,
  Calendar,
  User,
  Building,
  MapPin,
  Phone,
  Mail,
  FileText,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Download,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { formatDateShort } from '../../../utils/stringUtils';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES, getLeadStatusColor, formatRoleName } from '../../../utils/constants';
import { useLeads } from '../../../context/LeadContext';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  validateNumberInput,
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { truncateText } from '../../../utils/stringUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import ConvertLeadModal from './ConvertLeadModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

import { leadSourceService } from '../../../api/crm/config/configService';

// Map backend source to frontend display
const mapSourceToFrontend = (source) => {
  const sourceMap = {
    'website': 'Website',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'linkedin': 'LinkedIn',
    'google_ads': 'Google Ads',
    'whatsapp': 'WhatsApp',
    'referral': 'Referral',
    'cold_call': 'Cold Call',
    'walk_in': 'Walk In',
    'manual_entry': 'Manual Entry',
    'other': 'Other'
  };
  return sourceMap[source] || source || 'Manual Entry';
};

// Map frontend source to backend source (matching LeadListFilters)
const mapSourceToBackend = (source) => {
  const sourceMap = {
    'Website': 'website',
    'Facebook': 'facebook',
    'Instagram': 'instagram',
    'LinkedIn': 'linkedin',
    'Google Ads': 'google_ads',
    'WhatsApp': 'whatsapp',
    'Referral': 'referral',
    'Cold Call': 'cold_call',
    'Walk In': 'walk_in',
    'Manual Entry': 'manual_entry',
    'Other': 'other'
  };
  return sourceMap[source] || source?.toLowerCase() || 'other';
};

const ConvertOpportunity = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { leadStatuses, mapStageToStatusName } = useLeads();

  // Initial filter values from URL (for persistence across refresh)
  const initialSubTab = searchParams.get('subTab') || 'convert';
  const initialSearch = searchParams.get('search') || '';
  const initialSource = searchParams.get('source') || 'all';
  const initialTemperature = searchParams.get('temperature') || 'all';
  const initialQStartDate = searchParams.get('qStartDate') || '';
  const initialQEndDate = searchParams.get('qEndDate') || '';
  const initialLeadsPage = Number(searchParams.get('lPage')) || 1;
  const initialLeadsLimit = Number(searchParams.get('lLimit')) || 10;
  const initialLeadsSortBy = searchParams.get('lSortBy') || 'lastActivity';
  const initialLeadsSortOrder = searchParams.get('lSortOrder') || 'desc';
  const initialOppSearch = searchParams.get('oSearch') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialCStartDate = searchParams.get('cStartDate') || '';
  const initialCEndDate = searchParams.get('cEndDate') || '';
  const initialOppsPage = Number(searchParams.get('oPage')) || 1;
  const initialOppsLimit = Number(searchParams.get('oLimit')) || 10;
  const initialOppsSortBy = searchParams.get('oSortBy') || 'convertedAtDate';
  const initialOppsSortOrder = searchParams.get('oSortOrder') || 'desc';

  const [activeTab, setActiveTab] = useState(initialSubTab);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [sourceFilter, setSourceFilter] = useState(initialSource);
  const [temperatureFilter, setTemperatureFilter] = useState(initialTemperature);
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState(initialOppSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [editFormErrors, setEditFormErrors] = useState({});

  const [leadsItemsPerPage, setLeadsItemsPerPage] = useState(initialLeadsLimit);
  const [leadsCurrentPage, setLeadsCurrentPage] = useState(initialLeadsPage);
  const [opportunitiesItemsPerPage, setOpportunitiesItemsPerPage] = useState(initialOppsLimit);
  const [opportunitiesCurrentPage, setOpportunitiesCurrentPage] = useState(initialOppsPage);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState(null);

  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Date Range Filters
  const [qualifiedDateRange, setQualifiedDateRange] = useState({ startDate: initialQStartDate, endDate: initialQEndDate });
  const [convertedDateRange, setConvertedDateRange] = useState({ startDate: initialCStartDate, endDate: initialCEndDate });

  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else {
      return 'Unknown Lead';
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return { date: formattedDate, time: formattedTime };
  };

  // Lead Sources Query
  const { data: leadSources = [] } = useQuery({
    queryKey: ['leadSources'],
    queryFn: async () => {
      const response = await leadSourceService.getAll({ isActive: true });
      return response?.success && Array.isArray(response.data) ? response.data : [];
    }
  });

  // Qualified Leads Query
  const { data: qualifiedLeadsResponse, isLoading: loadingLeads } = useQuery({
    queryKey: ['qualifiedLeads', searchTerm, sourceFilter, temperatureFilter, qualifiedDateRange, leadsCurrentPage, leadsItemsPerPage, leadSources],
    queryFn: () => leadService.getQualifiedLeadsForConversion({
      search: searchTerm,
      page: leadsCurrentPage,
      limit: leadsItemsPerPage,
      source: sourceFilter !== 'all' ? sourceFilter : undefined,
      scoreLabel: temperatureFilter !== 'all' ? temperatureFilter.toLowerCase() : undefined,
      startDate: qualifiedDateRange.startDate || undefined,
      endDate: qualifiedDateRange.endDate || undefined
    }),
    placeholderData: keepPreviousData
  });

  const leadsForConversion = useMemo(() => {
    if (!qualifiedLeadsResponse?.success) return [];
    return qualifiedLeadsResponse.leads.map(lead => ({
      id: lead._id || lead.id,
      _id: lead._id,
      leadId: lead.leadId,
      firstName: lead.firstName,
      lastName: lead.lastName,
      name: getLeadName(lead),
      company: lead.company || 'No company',
      email: lead.email || 'No email',
      phone: lead.phone || 'No phone',
      source: (() => {
        if (!lead.source) return 'Manual Entry';
        if (typeof lead.source === 'object' && lead.source.name) return lead.source.name;
        const foundSource = leadSources.find(s => s._id === lead.source || s.id === lead.source || s.name === lead.source);
        return foundSource ? foundSource.name : typeof lead.source === 'string' ? lead.source : 'Manual Entry';
      })(),
      score: lead.score || 0,
      scoreLabel: lead.scoreLabel ? lead.scoreLabel.charAt(0).toUpperCase() + lead.scoreLabel.slice(1) : 'Cold',
      region: lead.region || 'N/A',
      leadValue: lead.leadValue ? Number(lead.leadValue) : 0,
      qualification: lead.stage,
      lastActivity: formatDate(lead.updatedAt || lead.createdAt),
      createdAt: lead.createdAt || lead.createdAtDate || new Date(),
      assignee: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : (lead.assignedTo || 'Unassigned'),
      owner: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : (lead.assignedTo || 'Unassigned'),
      status: mapStageToStatusName(lead.stage) || 'Open',
      description: lead.notes || 'No description available',
      notes: lead.internalNotes || lead.notes || 'No notes available',
      budget: lead.budget || 0,
      timeline: lead.timeline || 'Not specified',
      decisionMaker: lead.designation || 'Not specified',
      painPoints: [],
      requirements: []
    }));
  }, [qualifiedLeadsResponse, leadSources]);

  const leadsPagination = useMemo(() => {
    const pagination = qualifiedLeadsResponse?.pagination;
    return {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
      totalItems: pagination?.totalLeads || pagination?.totalItems || 0,
      hasNextPage: pagination?.hasNextPage || false,
      hasPrevPage: pagination?.hasPrevPage || false,
      limit: pagination?.limit || leadsItemsPerPage
    };
  }, [qualifiedLeadsResponse, leadsItemsPerPage]);

  // Converted Opportunities Query
  const { data: convertedOppsResponse, isLoading: loadingOpps } = useQuery({
    queryKey: ['convertedOpportunities', opportunitySearchTerm, statusFilter, convertedDateRange, opportunitiesCurrentPage, opportunitiesItemsPerPage],
    queryFn: () => leadService.getConvertedLeads({
      search: opportunitySearchTerm,
      page: opportunitiesCurrentPage,
      limit: opportunitiesItemsPerPage,
      stage: statusFilter !== 'all' ? statusFilter : undefined,
      startDate: convertedDateRange.startDate || undefined,
      endDate: convertedDateRange.endDate || undefined
    }),
    placeholderData: keepPreviousData
  });

  const convertedOpportunities = useMemo(() => {
    if (!convertedOppsResponse?.success) return [];
    return convertedOppsResponse.leads.map(lead => {
      const leadName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.name || 'N/A';
      const companyName = lead.company || 'N/A';
      const oppName = lead.opportunityName || `${companyName} - Opportunity`;
      const oppValue = lead.opportunityValue || 0;
      const oppExpectedCloseDate = lead.opportunityExpectedCloseDate;
      const oppDescription = lead.opportunityDescription || '';

      let expectedCloseFormatted = 'N/A';
      if (oppExpectedCloseDate) {
        const closeDate = new Date(oppExpectedCloseDate);
        expectedCloseFormatted = closeDate.toLocaleDateString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric'
        });
      }

      let convertedAtFormatted = 'N/A';
      if (lead.conversionDate) {
        const convertedDate = new Date(lead.conversionDate);
        convertedAtFormatted = convertedDate.toLocaleDateString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
      }

      const stageDisplay = mapStageToStatusName(lead.stage) || 'Converted';

      return {
        id: lead._id || lead.id,
        _id: lead._id,
        opportunityId: lead.leadId,
        opportunityName: oppName,
        leadName: leadName,
        company: companyName,
        stage: stageDisplay,
        stageValue: lead.stage,
        value: oppValue,
        probability: 0,
        expectedClose: expectedCloseFormatted,
        expectedCloseDate: oppExpectedCloseDate,
        owner: lead.assignedTo
          ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email || 'Unknown'
          : 'Unknown',
        assignedTo: lead.assignedTo,
        convertedAt: convertedAtFormatted,
        convertedAtDate: lead.conversionDate || lead.createdAt,
        convertedBy: lead.convertedBy,
        convertedByName: lead.convertedBy
          ? `${lead.convertedBy.firstName || ''} ${lead.convertedBy.lastName || ''}`.trim() || lead.convertedBy.email || 'Unknown'
          : 'Unknown',
        createdAt: lead.createdAt || new Date(),
        lastActivity: convertedAtFormatted,
        description: oppDescription,
        leadId: lead,
        hasQuotation: false,
        quotationId: null,
        quotationNumber: null,
        quotationStatus: null
      };
    });
  }, [convertedOppsResponse]);

  const opportunityPagination = useMemo(() => {
    const pagination = convertedOppsResponse?.pagination;
    return {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
      totalItems: pagination?.totalLeads || pagination?.totalItems || 0,
      hasNextPage: pagination?.hasNextPage || false,
      hasPrevPage: pagination?.hasPrevPage || false,
      limit: pagination?.limit || opportunitiesItemsPerPage
    };
  }, [convertedOppsResponse, opportunitiesItemsPerPage]);

  // Mutations
  const convertLeadMutation = useMutation({
    mutationFn: ({ leadId, opportunityData }) => leadService.convertToOpportunity(leadId, opportunityData),
    onSuccess: (result, variables) => {
      const { lead } = variables;
      const leadName = lead.firstName && lead.lastName
        ? `${lead.firstName} ${lead.lastName}`
        : lead.name || lead.firstName || 'Lead';
      const companyName = lead.company || 'N/A';

      showNotification(`Lead "${leadName}" from ${companyName} converted to opportunity successfully!`);
      setSelectedLead(null);
      setShowConvertModal(false);
      queryClient.invalidateQueries({ queryKey: ['qualifiedLeads'] });
      queryClient.invalidateQueries({ queryKey: ['convertedOpportunities'] });
      setActiveTab('converted');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.responseMessage || error.response?.data?.error || error.message || 'Failed to convert lead to opportunity';
      showNotification(errorMessage, 'error');
    }
  });

  const deleteOpportunityMutation = useMutation({
    mutationFn: (id) => leadService.deleteLead(id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Opportunity deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['convertedOpportunities'] });
        setShowDeleteConfirmModal(false);
        setOpportunityToDelete(null);
      } else {
        toast.error(result.error || 'Failed to delete opportunity');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete opportunity');
    }
  });


  const updateOpportunityMutation = useMutation({
    mutationFn: ({ id, data }) => leadService.updateLead(id, data),
    onSuccess: () => {
      showNotification('Opportunity updated successfully');
      queryClient.invalidateQueries({ queryKey: ['convertedOpportunities'] });
      setShowEditModal(false);
      setSelectedOpportunity(null);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.responseMessage || error.response?.data?.error || error.message || 'Failed to update opportunity';
      showNotification(errorMessage, 'error');
    }
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async ({ ids, type }) => {
      if (type === 'convert') {
        return leadService.bulkDeleteLeads(ids);
      } else {
        // Opportunities don't have a bulk delete API yet, so we loop or wait for one.
        // The original code loops through them.
        let successCount = 0;
        let lastError = null;
        for (const id of ids) {
          const result = await leadService.deleteLead(id);
          if (result.success) successCount++;
          else lastError = result.error;
        }
        return { success: successCount > 0, successCount, lastError };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(`Successfully deleted selected ${activeTab === 'convert' ? 'leads' : 'opportunities'}!`);
        setSelectedRows(new Set());
        queryClient.invalidateQueries({ queryKey: [activeTab === 'convert' ? 'qualifiedLeads' : 'convertedOpportunities'] });
      }
      if (result.error || result.lastError) {
        toast.error(result.error || result.lastError);
      }
      setShowBulkDeleteModal(false);
    },
    onError: (error) => {
      toast.error(error.message || 'An error occurred during bulk deletion');
    }
  });


  // Clear selected rows when tab changes
  useEffect(() => {
    setSelectedRows(new Set());
    setShowInlineSelectedFormats(false);
  }, [activeTab]);


  // Sorting state
  const [leadsSortColumn, setLeadsSortColumn] = useState(initialLeadsSortBy);
  const [leadsSortDirection, setLeadsSortDirection] = useState(initialLeadsSortOrder);
  const [oppsSortColumn, setOppsSortColumn] = useState(initialOppsSortBy);
  const [oppsSortDirection, setOppsSortDirection] = useState(initialOppsSortOrder);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = [
        'subTab', 'search', 'source', 'temperature', 'qStartDate', 'qEndDate',
        'lPage', 'lLimit', 'lSortBy', 'lSortOrder',
        'oSearch', 'status', 'cStartDate', 'cEndDate',
        'oPage', 'oLimit', 'oSortBy', 'oSortOrder',
      ];
      managedKeys.forEach(key => params.delete(key));

      if (activeTab && activeTab !== 'convert') params.set('subTab', activeTab);

      // Convert (qualified leads) tab filters
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (sourceFilter && sourceFilter !== 'all') params.set('source', sourceFilter);
      if (temperatureFilter && temperatureFilter !== 'all') params.set('temperature', temperatureFilter);
      if (qualifiedDateRange.startDate) params.set('qStartDate', qualifiedDateRange.startDate);
      if (qualifiedDateRange.endDate) params.set('qEndDate', qualifiedDateRange.endDate);
      if (leadsCurrentPage && leadsCurrentPage !== 1) params.set('lPage', String(leadsCurrentPage));
      if (leadsItemsPerPage && leadsItemsPerPage !== 10) params.set('lLimit', String(leadsItemsPerPage));
      if (leadsSortColumn && leadsSortColumn !== 'lastActivity') params.set('lSortBy', leadsSortColumn);
      if (leadsSortDirection && leadsSortDirection !== 'desc') params.set('lSortOrder', leadsSortDirection);

      // Converted (opportunities) tab filters
      if (opportunitySearchTerm.trim()) params.set('oSearch', opportunitySearchTerm.trim());
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (convertedDateRange.startDate) params.set('cStartDate', convertedDateRange.startDate);
      if (convertedDateRange.endDate) params.set('cEndDate', convertedDateRange.endDate);
      if (opportunitiesCurrentPage && opportunitiesCurrentPage !== 1) params.set('oPage', String(opportunitiesCurrentPage));
      if (opportunitiesItemsPerPage && opportunitiesItemsPerPage !== 10) params.set('oLimit', String(opportunitiesItemsPerPage));
      if (oppsSortColumn && oppsSortColumn !== 'convertedAtDate') params.set('oSortBy', oppsSortColumn);
      if (oppsSortDirection && oppsSortDirection !== 'desc') params.set('oSortOrder', oppsSortDirection);

      return params;
    }, { replace: true });
  }, [
    activeTab,
    searchTerm,
    sourceFilter,
    temperatureFilter,
    qualifiedDateRange,
    leadsCurrentPage,
    leadsItemsPerPage,
    leadsSortColumn,
    leadsSortDirection,
    opportunitySearchTerm,
    statusFilter,
    convertedDateRange,
    opportunitiesCurrentPage,
    opportunitiesItemsPerPage,
    oppsSortColumn,
    oppsSortDirection,
    setSearchParams,
  ]);

  const opportunityLostReasons = useMemo(() => {
    const leadData = selectedOpportunity?.leadId;
    if (Array.isArray(leadData?.stageHistory)) {
      const lostHistory = leadData.stageHistory.filter(h => {
        if (!h) return false;
        const isLostStage = h.stage === 'closed_lost' || 
                            (h.toStage && (h.toStage.value === 'closed_lost' || h.toStage === 'closed_lost'));
        return isLostStage && h.reason && h.reason.trim() !== '';
      });

      const sorted = [...lostHistory].sort((a, b) => {
        const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
        const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
        return dateB - dateA;
      });

      return sorted.map(h => ({
        reason: h.reason,
        date: h.changedAt
      }));
    }
    return [];
  }, [selectedOpportunity]);

  // Sorting handlers
  const handleLeadsSort = (column) => {
    if (leadsSortColumn === column) {
      setLeadsSortDirection(leadsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLeadsSortColumn(column);
      setLeadsSortDirection('asc');
    }
  };

  const handleOppsSort = (column) => {
    if (oppsSortColumn === column) {
      setOppsSortDirection(oppsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOppsSortColumn(column);
      setOppsSortDirection('asc');
    }
  };

  const getSortedLeadsForConversion = () => {
    if (!leadsSortColumn || !leadsForConversion || leadsForConversion.length === 0) return leadsForConversion;

    return [...leadsForConversion].sort((a, b) => {
      let aValue, bValue;

      if (leadsSortColumn === 'leadId') {
        aValue = a.leadId || '';
        bValue = b.leadId || '';
      } else if (leadsSortColumn === 'leadDetails') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (leadsSortColumn === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (leadsSortColumn === 'score') {
        aValue = Number(a.score) || 0;
        bValue = Number(b.score) || 0;
      } else if (leadsSortColumn === 'temperature') {
        aValue = (a.scoreLabel || '').toLowerCase();
        bValue = (b.scoreLabel || '').toLowerCase();
      } else if (leadsSortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (leadsSortColumn === 'owner') {
        aValue = (a.assignee || a.owner || '').toLowerCase();
        bValue = (b.assignee || b.owner || '').toLowerCase();
      } else if (leadsSortColumn === 'lastActivity') {
        // Use the raw date if available, otherwise fallback
        aValue = new Date(a.lastActivity?.date || a.lastActivity || 0).getTime();
        bValue = new Date(b.lastActivity?.date || b.lastActivity || 0).getTime();
      } else if (leadsSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[leadsSortColumn] || '';
        bValue = b[leadsSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return leadsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return leadsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortedConvertedOpportunities = () => {
    if (!oppsSortColumn || !convertedOpportunities || convertedOpportunities.length === 0) return convertedOpportunities;

    return [...convertedOpportunities].sort((a, b) => {
      let aValue, bValue;

      if (oppsSortColumn === 'opportunity') {
        aValue = (a.opportunityName || '').toLowerCase();
        bValue = (b.opportunityName || '').toLowerCase();
      } else if (oppsSortColumn === 'leadDetails') {
        aValue = (a.leadName || '').toLowerCase();
        bValue = (b.leadName || '').toLowerCase();
      } else if (oppsSortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (oppsSortColumn === 'status') {
        aValue = (a.stage).toLowerCase();
        bValue = (b.stage).toLowerCase();
      } else if (oppsSortColumn === 'expectedClose') {
        aValue = new Date(a.expectedCloseDate || 0).getTime();
        bValue = new Date(b.expectedCloseDate || 0).getTime();
      } else if (oppsSortColumn === 'convertedAtDate') {
        aValue = new Date(a.convertedAtDate || 0).getTime();
        bValue = new Date(b.convertedAtDate || 0).getTime();
      } else if (oppsSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (oppsSortColumn === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (oppsSortColumn === 'probability') {
        aValue = Number(a.probability) || 0;
        bValue = Number(b.probability) || 0;
      } else {
        aValue = a[oppsSortColumn] || '';
        bValue = b[oppsSortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return oppsSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return oppsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const baseExportFilters = useMemo(() => {
    const view = activeTab === 'convert' ? 'qualified' : 'converted';
    if (activeTab === 'convert') {
      return {
        view,
        search: searchTerm.trim() || undefined,
        source: sourceFilter !== 'all' ? mapSourceToBackend(sourceFilter) : undefined,
        scoreLabel: temperatureFilter !== 'all' ? temperatureFilter.toLowerCase() : undefined,
        startDate: qualifiedDateRange.startDate || undefined,
        endDate: qualifiedDateRange.endDate || undefined,
      };
    }
    return {
      view,
      search: opportunitySearchTerm.trim() || undefined,
      stage: statusFilter !== 'all' ? statusFilter : undefined,
      startDate: convertedDateRange.startDate || undefined,
      endDate: convertedDateRange.endDate || undefined,
    };
  }, [
    activeTab,
    searchTerm,
    sourceFilter,
    temperatureFilter,
    qualifiedDateRange.startDate,
    qualifiedDateRange.endDate,
    opportunitySearchTerm,
    statusFilter,
    convertedDateRange.startDate,
    convertedDateRange.endDate,
  ]);

  const exportSortBy = useMemo(
    () =>
      activeTab === 'convert'
        ? ({
            leadId: 'leadId',
            leadDetails: 'firstName',
            source: 'source',
            score: 'score',
            temperature: 'scoreLabel',
            status: 'stage',
            owner: 'updatedAt',
            lastActivity: 'updatedAt',
            createdAt: 'createdAt',
          }[leadsSortColumn] || 'updatedAt')
        : ({
            opportunity: 'opportunityName',
            leadDetails: 'firstName',
            value: 'opportunityValue',
            status: 'stage',
            expectedClose: 'opportunityExpectedCloseDate',
            convertedAtDate: 'conversionDate',
            owner: 'updatedAt',
            createdAt: 'createdAt',
            probability: 'conversionDate',
          }[oppsSortColumn] || 'conversionDate'),
    [activeTab, leadsSortColumn, oppsSortColumn]
  );

  const currentPageIdsForExport = useMemo(() => {
    const items =
      activeTab === 'convert' ? getSortedLeadsForConversion() : getSortedConvertedOpportunities();
    return items.map((item) => String(item._id || item.id)).filter(Boolean);
  }, [
    activeTab,
    leadsForConversion,
    convertedOpportunities,
    leadsSortColumn,
    leadsSortDirection,
    oppsSortColumn,
    oppsSortDirection,
  ]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageIdsForExport)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-convert-opportunity',
            format,
            scope,
            filters: {
              ...baseExportFilters,
              ...(overrideFilters != null ? overrideFilters : {}),
            },
            page:
              overridePage ??
              (activeTab === 'convert' ? leadsCurrentPage : opportunitiesCurrentPage),
            limit:
              overrideLimit ??
              (activeTab === 'convert' ? leadsItemsPerPage : opportunitiesItemsPerPage),
            sortBy: exportSortBy,
            sortOrder:
              activeTab === 'convert' ? leadsSortDirection : oppsSortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `convert-opportunity-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      activeTab,
      leadsCurrentPage,
      leadsItemsPerPage,
      opportunitiesCurrentPage,
      opportunitiesItemsPerPage,
      exportSortBy,
      leadsSortDirection,
      oppsSortDirection,
      currentPageIdsForExport,
    ]
  );

  const handleConvertOpportunitySelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} row(s) exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleConvertOpportunityScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const pageItems =
        activeTab === 'convert'
          ? getSortedLeadsForConversion()
          : getSortedConvertedOpportunities();
      const pag = activeTab === 'convert' ? leadsPagination : opportunityPagination;

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered') {
          const total = pag?.totalItems ?? 0;
          if (total === 0) {
            toast.error('No records match the current filters.');
            return;
          }
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, startDate, endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      activeTab,
      baseExportFilters,
      leadsPagination,
      opportunityPagination,
      leadsForConversion,
      convertedOpportunities,
      requestBackendExport,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);


  // Handle row selection
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    // Check if all items on current page are already selected
    const allSelected = items.every(item => selectedRows.has(item._id || item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        // Deselect all items on current page
        items.forEach(item => newSelection.delete(item._id || item.id));
      } else {
        // Select all items on current page
        items.forEach(item => newSelection.add(item._id || item.id));
      }
      return newSelection;
    });
  };


  // Notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getQualificationColor = (qualification) => {
    switch (qualification) {
      case 'Highly Qualified': return 'bg-green-100 text-green-800';
      case 'Qualified': return 'bg-blue-100 text-blue-800';
      case 'Disqualified': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    return getLeadStatusColor(stage);
  };

  const handleConvertLead = (lead) => {
    setSelectedLead(lead);
    setShowConvertModal(true);
  };

  const handleConvertToOpportunity = async (lead, opportunityData) => {
    convertLeadMutation.mutate({ leadId: lead._id || lead.id || lead.leadId, opportunityData, lead });
  };

  const handleViewLead = (leadId) => {
    navigate(`/lead_detail_360/${leadId}`);
  };

  const handleViewOpportunity = (opportunityId) => {
    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowViewModal(true);
    }
  };

  const handleEditOpportunity = (opportunityId) => {
    const opportunity = convertedOpportunities.find(opp => opp.id === opportunityId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setShowEditModal(true);
    }
  };

  const handleDeleteOpportunity = (opportunityId) => {
    setOpportunityToDelete(opportunityId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteOpportunity = async () => {
    if (!opportunityToDelete) return;
    deleteOpportunityMutation.mutate(opportunityToDelete);
  };

  const cancelDeleteOpportunity = () => {
    setShowDeleteConfirmModal(false);
    setOpportunityToDelete(null);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    const selectedIds = Array.from(selectedRows);
    if (activeTab === 'convert') {
      bulkDeleteMutation.mutate({ ids: selectedIds, type: 'convert' });
    } else {
      const oppsToDelete = convertedOpportunities.filter(opp =>
        selectedRows.has(opp._id || opp.id) &&
        opp.stage !== 'Closed Won' &&
        opp.stageValue !== 'closed_won'
      );
      const idsToDelete = oppsToDelete.map(opp => opp._id || opp.id);
      bulkDeleteMutation.mutate({ ids: idsToDelete, type: 'converted' });
    }
  };

  const handleCreateQuotation = (opportunity) => {
    const oppId = opportunity._id || opportunity.id;
    navigate(`/crm/quotation-proposal/quote-builder?opportunityId=${oppId}`);
  };

  const handleViewQuotation = (opportunity) => {
    const quoteId = opportunity.quotationId || opportunity.quotationNumber;
    if (quoteId) {
      navigate(`/crm/quotation-proposal/send-track/${quoteId}`);
    } else {
      showNotification('Quotation ID not found', 'error');
    }
  };

  const handleSaveOpportunity = async (updatedData) => {
    if (!selectedOpportunity) return;
    const leadUpdateData = {
      opportunityName: updatedData.name || updatedData.opportunityName,
      opportunityValue: updatedData.amount || updatedData.value,
      opportunityExpectedCloseDate: updatedData.expectedCloseDate,
      opportunityDescription: updatedData.description,
      stage: updatedData.stage,
      statusChangeReason: updatedData.statusChangeReason || updatedData.lossReason
    };
    updateOpportunityMutation.mutate({ id: selectedOpportunity._id || selectedOpportunity.id, data: leadUpdateData });
  };


  // Handle items per page change for leads
  const handleLeadsItemsPerPageChange = (newLimit) => {
    setLeadsItemsPerPage(newLimit);
    setLeadsCurrentPage(1); // Reset to first page when changing limit
  };

  // Handle page change for leads
  const handleLeadsPageChange = (page) => {
    setLeadsCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle items per page change for opportunities
  const handleOpportunitiesItemsPerPageChange = (newLimit) => {
    setOpportunitiesItemsPerPage(newLimit);
    setOpportunitiesCurrentPage(1); // Reset to first page when changing limit
  };

  // Handle page change for opportunities
  const handleOpportunitiesPageChange = (page) => {
    setOpportunitiesCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Aliases for backward compatibility
  const handleItemsPerPageChange = handleOpportunitiesItemsPerPageChange;
  const handlePageChange = handleOpportunitiesPageChange;

  // Check if there are active filters for qualified leads
  const hasActiveLeadsFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' ||
      sourceFilter !== 'all' ||
      temperatureFilter !== 'all' ||
      qualifiedDateRange.startDate !== '' ||
      qualifiedDateRange.endDate !== ''
    );
  }, [searchTerm, sourceFilter, temperatureFilter, qualifiedDateRange]);

  // Check if there are active filters for converted opportunities
  const hasActiveOpportunitiesFilters = useMemo(() => {
    return (
      opportunitySearchTerm.trim() !== '' ||
      statusFilter !== 'all' ||
      convertedDateRange.startDate !== '' ||
      convertedDateRange.endDate !== ''
    );
  }, [opportunitySearchTerm, statusFilter, convertedDateRange]);

  // Clear filters for qualified leads
  const handleClearLeadsFilters = () => {
    setSearchTerm('');
    setSourceFilter('all');
    setTemperatureFilter('all');
    setQualifiedDateRange({ startDate: '', endDate: '' });
    setLeadsCurrentPage(1);
  };

  // Clear filters for converted opportunities
  const handleClearOpportunitiesFilters = () => {
    setOpportunitySearchTerm('');
    setStatusFilter('all');
    setConvertedDateRange({ startDate: '', endDate: '' });
    setOpportunitiesCurrentPage(1);
  };

  return (
    <div className="space-y-6 min-h-0">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg max-w-sm ${notification.type === 'success'
          ? 'bg-green-500 text-white'
          : notification.type === 'error'
            ? 'bg-red-500 text-white'
            : notification.type === 'info'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="text-sm font-medium">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Convert to Opportunity</h1>
            <p className="text-gray-600">Convert qualified leads into sales opportunities</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="lead_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={
                  selectedRows.size > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : ''
                }
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleConvertOpportunitySelectedFormatExport}
              />
            </div>
          </PermissionGate>

          {selectedRows.size > 0 && user?.role === 'super_admin' && (
            <PermissionGate permission="lead_management" action="delete">
              <Button
                variant="outline"
                icon={bulkDeleteMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete (${selectedRows.size})`}
              </Button>
            </PermissionGate>
          )}

          <PermissionGate permission="lead_management" action="read">
            <Button
              variant="outline"
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => navigate('/lead_list_filters')}
            >
              View All Leads
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'convert', label: 'Qualified Leads', permission: 'lead_management' },
              { key: 'converted', label: 'Converted Opportunities', permission: 'lead_management' }
            ].map((tab) => (
              <PermissionGate key={tab.key} permission={tab.permission} action="read">
                <button
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                    ? 'border-agrosaf-green text-agrosaf-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              </PermissionGate>
            ))}
          </nav>
        </div>

        <div className="p-6 overflow-x-auto">
          {activeTab === 'convert' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="relative flex-1 w-full sm:w-auto sm:min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      const sanitized = handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search);
                      setSearchTerm(sanitized);
                    }}
                    placeholder="Search by ID, name, email, company..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    maxLength={FIELD_LIMITS.search}
                    autoFocus
                  />
                </div>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full sm:w-auto sm:min-w-[180px]"
                >
                  <option value="all">All Sources</option>
                  {leadSources.map(source => (
                    <option key={source._id || source.id || source.name} value={source._id || source.id || source.name}>
                      {source.name}
                    </option>
                  ))}
                </select>
                <select
                  value={temperatureFilter}
                  onChange={(e) => setTemperatureFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full sm:w-auto sm:min-w-[150px]"
                >
                  <option value="all">All Temperatures</option>
                  <option value="Hot">Hot</option>
                  <option value="Warm">Warm</option>
                  <option value="Cold">Cold</option>
                </select>
                <div className="flex-shrink-0">
                  <DateRangeFilter
                    startDate={qualifiedDateRange.startDate}
                    endDate={qualifiedDateRange.endDate}
                    onChange={setQualifiedDateRange}
                  />
                </div>
                {hasActiveLeadsFilters && (
                  <PermissionGate permission="lead_management" action="read">
                    <Button
                      variant="outline"
                      icon={<X className="h-4 w-4" />}
                      onClick={handleClearLeadsFilters}
                      className="flex-shrink-0 whitespace-nowrap"
                    >
                      Clear
                    </Button>
                  </PermissionGate>
                )}
              </div>

              <div className="space-y-4">
                {loadingLeads ? (
                  <div className="text-center py-12">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading leads...</span>
                    </div>
                  </div>
                ) : leadsForConversion.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Qualified Leads Ready for Conversion</h3>
                    <p className="text-gray-600 mb-4">You don't have any qualified leads ready to convert to opportunities yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-5 py-3 text-left">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                              checked={leadsForConversion.length > 0 && leadsForConversion.every(item => selectedRows.has(item._id || item.id))}
                              onChange={() => handleSelectAll(leadsForConversion)}
                            />
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('leadId')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead ID</span>
                              {leadsSortColumn === 'leadId' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px] cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('leadDetails')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Lead Details</span>
                              {leadsSortColumn === 'leadDetails' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('source')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Source</span>
                              {leadsSortColumn === 'source' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('score')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Score</span>
                              {leadsSortColumn === 'score' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('temperature')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Temperature</span>
                              {leadsSortColumn === 'temperature' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('status')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {leadsSortColumn === 'status' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('owner')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Owner</span>
                              {leadsSortColumn === 'owner' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('lastActivity')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Last Activity</span>
                              {leadsSortColumn === 'lastActivity' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th
                            className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLeadsSort('createdAt')}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Created At</span>
                              {leadsSortColumn === 'createdAt' ? (
                                leadsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </th>
                          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getSortedLeadsForConversion().map((lead) => (
                          <tr key={lead.id} className="hover:bg-gray-50">
                            <td className="px-5 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                                checked={selectedRows.has(lead._id || lead.id)}
                                onChange={() => handleSelectRow(lead._id || lead.id)}
                              />
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900 font-medium font-mono">
                              {lead.leadId || 'No ID'}
                            </td>
                            <td className="px-5 py-4 max-w-[240px]">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className="min-w-0 flex-1 overflow-hidden">
                                  <div className="text-sm font-medium text-gray-900 truncate capitalize" title={lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`}>{lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`}</div>
                                  <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                                  <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">{mapSourceToFrontend(lead.source)}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                              {lead.score}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(lead.scoreLabel)}`}>
                                {lead.scoreLabel}
                              </span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(lead.status)}`}>{lead.status}</span>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">{lead.assignee || lead.owner || 'Unassigned'}</td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <div className="text-gray-900">{lead.lastActivity?.date || lead.lastActivity}</div>
                              {lead.lastActivity?.time && <div className="text-xs text-gray-500">{lead.lastActivity.time}</div>}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm">
                              <div className="text-gray-900">{formatDate(lead.createdAt).date}</div>
                              <div className="text-xs text-gray-500">{formatDate(lead.createdAt).time}</div>
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <PermissionGate permission="lead_management" action="read">
                                  <button
                                    onClick={() => handleViewLead(lead.id)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="View Lead"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="lead_management" action="create">
                                  <span>
                                    <Button
                                      onClick={() => handleConvertLead(lead)}
                                      variant="primary"
                                      icon={<ArrowRight className="h-4 w-4" />}
                                      size="sm"
                                    >
                                      Convert
                                    </Button>
                                  </span>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination for Qualified Leads */}
              {(() => {
                const leadsTotalItems = Number(leadsPagination?.totalItems || leadsForConversion.length || 0);
                const leadsTotalPages = Number(leadsPagination?.totalPages || 1);
                const effectivePaginationLimit = leadsPagination?.limit || leadsItemsPerPage || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !loadingLeads && leadsTotalPages > 1;

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((leadsPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((leadsPagination.currentPage || 1) * effectivePaginationLimit, leadsTotalItems)} of {leadsTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={leadsItemsPerPage}
                            onChange={(e) => handleLeadsItemsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={3}>3</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleLeadsPageChange((leadsPagination.currentPage || 1) - 1)}
                          disabled={leadsPagination.hasPrevPage === false || (leadsPagination.currentPage || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = leadsPagination.totalPages || 1;
                            const currentPageNum = leadsPagination.currentPage || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handleLeadsPageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handleLeadsPageChange((leadsPagination.currentPage || 1) + 1)}
                          disabled={leadsPagination.hasNextPage === false || (leadsPagination.currentPage || 1) >= (leadsPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {activeTab === 'converted' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="relative flex-1 w-full sm:w-auto sm:min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={opportunitySearchTerm}
                    placeholder="Search by ID, name, lead name, company..."
                    onChange={(e) => {
                      const sanitized = handleSearchInputChange(e, setOpportunitySearchTerm, FIELD_LIMITS.search);
                      setOpportunitySearchTerm(sanitized);
                    }}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    maxLength={FIELD_LIMITS.search}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full sm:w-auto sm:min-w-[180px] font-medium text-gray-700 bg-white shadow-sm hover:border-gray-400 transition-colors"
                >
                  <option value="all">All Status</option>
                  {leadStatuses.map(status => (
                    <option key={status._id || status.id || status.value} value={status.value}>
                      {status.name}
                    </option>
                  ))}
                </select>
                <div className="flex-shrink-0">
                  <DateRangeFilter
                    startDate={convertedDateRange.startDate}
                    endDate={convertedDateRange.endDate}
                    onChange={setConvertedDateRange}
                  />
                </div>
                {hasActiveOpportunitiesFilters && (
                  <PermissionGate permission="lead_management" action="read">
                    <Button
                      variant="outline"
                      icon={<X className="h-4 w-4" />}
                      onClick={handleClearOpportunitiesFilters}
                      className="flex-shrink-0 whitespace-nowrap"
                    >
                      Clear
                    </Button>
                  </PermissionGate>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                          checked={convertedOpportunities.length > 0 && convertedOpportunities.every(item => selectedRows.has(item._id || item.id))}
                          onChange={() => handleSelectAll(convertedOpportunities)}
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('opportunity')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Opportunity</span>
                          {oppsSortColumn === 'opportunity' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('leadDetails')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Lead Details</span>
                          {oppsSortColumn === 'leadDetails' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 select-none">
                        <div className="flex items-center space-x-1">
                          <span>Assignee</span>
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('value')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Value</span>
                          {oppsSortColumn === 'value' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {oppsSortColumn === 'status' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('expectedClose')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Expected Close</span>
                          {oppsSortColumn === 'expectedClose' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('convertedAtDate')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Converted At</span>
                          {oppsSortColumn === 'convertedAtDate' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleOppsSort('createdAt')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created At</span>
                          {oppsSortColumn === 'createdAt' ? (
                            oppsSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loadingOpps ? (
                      <tr>
                        <td colSpan="10" className="px-6 py-12 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600">Loading converted opportunities...</span>
                          </div>
                        </td>
                      </tr>
                    ) : convertedOpportunities.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-6 py-12 text-center">
                          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Converted Opportunities</h3>
                          <p className="text-gray-600">No opportunities have been converted from leads yet.</p>
                        </td>
                      </tr>
                    ) : (
                      getSortedConvertedOpportunities().map((opportunity) => (
                        <tr key={opportunity.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                              checked={selectedRows.has(opportunity._id || opportunity.id)}
                              onChange={() => handleSelectRow(opportunity._id || opportunity.id)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap w-1/4">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={opportunity.opportunityName}>{truncateText(opportunity.opportunityName, 30)}</div>
                              <div className="text-xs text-gray-500 truncate" title={`ID: ${opportunity.opportunityId || 'N/A'}`}>
                                ID: {opportunity.opportunityId || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap w-1/4">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={opportunity.leadName}>{truncateText(opportunity.leadName, 25)}</div>
                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={opportunity.company}>{truncateText(opportunity.company, 25)}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap w-1/6">
                            <div>
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={opportunity.assignedTo ? `${opportunity.assignedTo.firstName || ''} ${opportunity.assignedTo.lastName || ''}`.trim() : 'Unassigned'}>
                                {opportunity.assignedTo ? `${opportunity.assignedTo.firstName || ''} ${opportunity.assignedTo.lastName || ''}`.trim() : 'Unassigned'}
                              </div>
                              {opportunity.assignedTo?.email && (
                                <div className="text-xs text-gray-500 truncate" title={opportunity.assignedTo.email}>
                                  {opportunity.assignedTo.email}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="truncate" title={`₹${typeof opportunity.value === 'number' ? formatIndianAmount(opportunity.value) : '0'}`}>
                              ₹{typeof opportunity.value === 'number' ? formatIndianAmount(opportunity.value) : '0'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opportunity.stage)}`}>
                              {opportunity.stage == 'Converted' ? 'Open' : opportunity.stage}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="truncate" title={opportunity.expectedClose || 'N/A'}>
                              {opportunity.expectedClose || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="text-gray-900">{formatDate(opportunity.convertedAtDate).date}</div>
                            <div className="text-xs text-gray-500">{formatDate(opportunity.convertedAtDate).time}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="text-gray-900">{formatDate(opportunity.createdAt).date}</div>
                            <div className="text-xs text-gray-500">{formatDate(opportunity.createdAt).time}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <PermissionGate permission="lead_management" action="read">
                                <button
                                  onClick={() => handleViewOpportunity(opportunity.id)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View Opportunity"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="lead_management" action="update">
                                <button
                                  onClick={() => handleEditOpportunity(opportunity.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Opportunity"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              {user?.role === 'super_admin' && (
                                <PermissionGate permission="lead_management" action="delete">
                                  <span title={opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won' ? "Closed Won opportunities cannot be deleted" : ""}>
                                    <button
                                      onClick={() => (opportunity.stage !== 'Closed Won' && opportunity.stageValue !== 'closed_won') && handleDeleteOpportunity(opportunity.id)}
                                      className={`${opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                                      disabled={opportunity.stage === 'Closed Won' || opportunity.stageValue === 'closed_won'}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </span>
                                </PermissionGate>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(() => {
                const opportunitiesTotalItems = Number(opportunityPagination?.totalItems || convertedOpportunities.length || 0);
                const opportunitiesTotalPages = Number(opportunityPagination?.totalPages || 1);
                const effectivePaginationLimit = opportunityPagination?.limit || opportunitiesItemsPerPage || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !loadingOpps && opportunitiesTotalPages > 1;

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((opportunityPagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((opportunityPagination.currentPage || 1) * effectivePaginationLimit, opportunitiesTotalItems)} of {opportunitiesTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={opportunitiesItemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={3}>3</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange((opportunityPagination.currentPage || 1) - 1)}
                          disabled={opportunityPagination.hasPrevPage === false || (opportunityPagination.currentPage || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = opportunityPagination.totalPages || 1;
                            const currentPageNum = opportunityPagination.currentPage || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handlePageChange((opportunityPagination.currentPage || 1) + 1)}
                          disabled={opportunityPagination.hasNextPage === false || (opportunityPagination.currentPage || 1) >= (opportunityPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Convert Lead Modal */}
      <ConvertLeadModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        lead={selectedLead}
        onSubmit={handleConvertToOpportunity}
      />

      {/* View Opportunity Modal */}
      {showViewModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Opportunity Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">


                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Opportunity Name</p>
                      <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.opportunityName}>{selectedOpportunity.opportunityName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Lead Name</p>
                      <p className="text-sm font-medium text-gray-900 break-words capitalize" title={selectedOpportunity.leadName}>{selectedOpportunity.leadName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.company}>{selectedOpportunity.company || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Opportunity Value</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={`₹${selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}`}>
                        ₹{selectedOpportunity.value ? formatIndianAmount(selectedOpportunity.value) : '0'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Expected Close</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.expectedClose}>{selectedOpportunity.expectedClose || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadId?.email || 'N/A'}>{selectedOpportunity.leadId?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="text-sm font-medium text-gray-900 break-words" title={selectedOpportunity.leadId?.phone || 'N/A'}>{selectedOpportunity.leadId?.phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize" title={selectedOpportunity.description || 'No description'}>{selectedOpportunity.description || 'No description'}</p>
                  </div>
                </div>

                {/* Loss Reason History */}
                {opportunityLostReasons.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Loss Reason History
                    </h4>
                    <div className="space-y-3">
                      {opportunityLostReasons.map((lr, index) => {
                        const reasonText = typeof lr === 'object' ? lr.reason : lr;
                        const reasonDate = typeof lr === 'object' ? lr.date : null;

                        return (
                          <div key={index} className={`${index !== 0 ? 'border-t border-red-100 pt-3' : ''}`}>
                            <p className="text-sm text-gray-800 first-letter:capitalize">{reasonText}</p>
                            {reasonDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(reasonDate).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Timeline and Conversion Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Timeline</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Converted At</p>
                        <p className="text-sm font-medium text-gray-900">{selectedOpportunity.convertedAt || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Lead Created</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedOpportunity.leadId?.createdAt
                            ? new Date(selectedOpportunity.leadId.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Converted By</h4>
                    {selectedOpportunity.convertedBy ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {`${selectedOpportunity.convertedBy.firstName || ''} ${selectedOpportunity.convertedBy.lastName || ''}`.trim() || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Employee ID</p>
                          <p className="text-sm font-medium text-gray-900 uppercase">
                            {selectedOpportunity.convertedBy.employeeId || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Role</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedOpportunity.convertedBy.role ? formatRoleName(selectedOpportunity.convertedBy.role) : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Department</p>
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {selectedOpportunity.convertedBy.department || 'N/A'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Conversion details not available for legacy records</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
                <PermissionGate permission="lead_management" action="update">
                  <span title={selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won' ? "Closed Won opportunities cannot be edited" : ""}>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (selectedOpportunity.stage !== 'Closed Won' && selectedOpportunity.stageValue !== 'closed_won') {
                          setShowViewModal(false);
                          handleEditOpportunity(selectedOpportunity.id);
                        }
                      }}
                      disabled={selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won'}
                      className={`${selectedOpportunity.stage === 'Closed Won' || selectedOpportunity.stageValue === 'closed_won' ? 'bg-gray-400 cursor-not-allowed' : 'bg-agrosaf-green hover:bg-agrosaf-darkGreen'}`}
                    >
                      Edit Opportunity
                    </Button>
                  </span>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      <OpportunityEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        opportunity={selectedOpportunity}
        onSave={handleSaveOpportunity}
        isSaving={updateOpportunityMutation.isPending}
      />

      {/* Delete Opportunity Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <button
                  onClick={cancelDeleteOpportunity}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this opportunity? This action cannot be undone.
                </p>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={cancelDeleteOpportunity}
                >
                  Cancel
                </Button>
                <PermissionGate permission="lead_management" action="delete">
                  <Button
                    variant="primary"
                    onClick={confirmDeleteOpportunity}
                    className="bg-red-500 hover:bg-red-700"
                  >
                    Delete Opportunity
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title={`Export ${activeTab === 'convert' ? 'Qualified Leads' : 'Converted Opportunities'}`}
        showDateRange
        scopedFormatExport={{
          onExport: handleConvertOpportunityScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                Bulk Delete {activeTab === 'convert' ? 'Qualified Leads' : 'Converted Opportunities'}
              </h3>
              <button
                onClick={() => !bulkDeleteMutation.isPending && setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={bulkDeleteMutation.isPending}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected {activeTab === 'convert' ? 'leads' : 'opportunities'}? {activeTab === 'converted' && "Any 'Closed Won' opportunities will be skipped."} This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={bulkDeleteMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmBulkDelete}
                disabled={bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete All Selected (${selectedRows.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvertOpportunity;
